import { companyRequest } from '@/company/api';

// Read-only: what shared work runs on. Its connections change only through
// the item's chat (update_work_connection), never from a screen.

export type ConnectionGrantKind = 'result' | 'agent' | 'skill';
export type GrantOption = { id: string; label: string };
export type ConnectionGrantAuthority = {
  connectionId: string;
  name: string | null;
  provider?: string | null;
  enabled: boolean;
  available: boolean;
  // Why an unavailable grant paused: its grantor's access ended, or someone
  // else changed the App's code since the grantor confirmed it.
  pausedFor?: 'access_ended' | 'code_changed';
  operations: GrantOption[];
  grant: {
    grantorUserId: string | null;
    grantorName?: string | null;
    allowChanges: boolean;
  } | null;
};
export type ConnectionGrantState = {
  canEdit: boolean;
  items: ConnectionGrantAuthority[];
};

type RawOption = string | { id?: unknown; label?: unknown };
const options = (value: unknown): GrantOption[] =>
  Array.isArray(value)
    ? value.flatMap((entry: RawOption) => {
        if (typeof entry === 'string') return [{ id: entry, label: entry }];
        if (!entry || typeof entry.id !== 'string') return [];
        return [{ id: entry.id, label: typeof entry.label === 'string' ? entry.label : entry.id }];
      })
    : [];
const authority = (raw: unknown): ConnectionGrantAuthority | null => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const value = raw as Record<string, unknown>;
  if (typeof value.connectionId !== 'string') return null;
  const grant =
    value.grant && typeof value.grant === 'object'
      ? (value.grant as Record<string, unknown>)
      : null;
  const access =
    value.access && typeof value.access === 'object'
      ? (value.access as Record<string, unknown>)
      : null;
  // What the grant reads: its own operations, narrowed to its access when given.
  const allowed = options(access?.operations).map(({ id }) => id);
  const operations = options(value.operations);
  return {
    connectionId: value.connectionId,
    name: typeof value.name === 'string' ? value.name : null,
    provider: typeof value.provider === 'string' ? value.provider : null,
    enabled: value.enabled !== false,
    available: value.available !== false,
    pausedFor: value.pausedFor === 'code_changed' ? 'code_changed' : 'access_ended',
    operations: allowed.length ? operations.filter(({ id }) => allowed.includes(id)) : operations,
    grant: grant
      ? {
          grantorUserId: typeof grant.grantorUserId === 'string' ? grant.grantorUserId : null,
          grantorName: typeof grant.grantorName === 'string' ? grant.grantorName : null,
          allowChanges: grant.allowChanges === true,
        }
      : null,
  };
};
const normalize = (value: unknown): ConnectionGrantState => {
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const items = Array.isArray(raw.items)
    ? raw.items.flatMap((entry) => {
        const item = authority(entry);
        return item ? [item] : [];
      })
    : [];
  return { canEdit: raw.canEdit === true, items };
};
const path = (kind: ConnectionGrantKind, id: string) =>
  `/api/v1/company/work-connections/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`;

export const connectionGrantsApi = {
  get: (kind: ConnectionGrantKind, id: string, session?: typeof fetch) =>
    companyRequest<unknown>(path(kind, id), 'GET', undefined, session).then(normalize),
};
